import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";

/** Las dos mitades de un producto ya creado. */
export type ProductTab = "producto" | "detalles";

export interface ProductTabsProps {
  /** El id del producto: las dos direcciones cuelgan de él. */
  id: string;
  active: ProductTab;
  className?: string;
}

const TABS: readonly { key: ProductTab; label: string; path: string }[] = [
  { key: "producto", label: "Producto", path: "" },
  { key: "detalles", label: "Detalles", path: "/detalles" },
];

/**
 * ═══════════════════════════════════════════════════════════════════
 * Las dos pantallas de un producto ya creado.
 *
 * ── Por qué solo al EDITAR ──────────────────────────────────────────
 * Los detalles editoriales se escriben SOBRE un producto que ya existe:
 * son bloques opcionales de su página pública. Ofrecerlos durante el
 * alta convertiría "dar de alta un producto" en un trabajo de una tarde,
 * que es justo lo que se quiso evitar sacándolos del asistente. Por eso
 * esta fila no se pinta en `/admin/products/new`: hasta que el producto
 * no tiene id, no hay a dónde ir.
 *
 * ── Enlaces, no pestañas ────────────────────────────────────────────
 * Son dos DIRECCIONES distintas, cada una con su formulario y su
 * guardado. Un `tablist` de ARIA promete que el contenido cambia sin
 * salir de la página y que las flechas del teclado se mueven entre
 * pestañas; aquí lo que ocurre es una navegación, y decirlo mal es peor
 * que no decirlo. `aria-current="page"` es lo que corresponde.
 *
 * Y son enlaces DE VERDAD (`<Link>`, no un `<button>` que navega): así se
 * pueden abrir en otra pestaña, copiar y compartir — la dirección de los
 * detalles de un producto es algo que se manda por mensaje.
 * ═══════════════════════════════════════════════════════════════════
 */
export function ProductTabs({ id, active, className }: ProductTabsProps) {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t("Secciones del producto")}
      className={cn(
        "mb-3 inline-flex gap-1 rounded-ds-pill bg-ds-mist/50 p-1",
        className,
      )}
    >
      {TABS.map((tab) => {
        const selected = tab.key === active;
        return (
          <Link
            key={tab.key}
            to={`/admin/products/${id}${tab.path}`}
            aria-current={selected ? "page" : undefined}
            className={cn(
              "inline-flex items-center rounded-ds-pill px-4 py-2 text-[13px] font-semibold transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50",
              selected
                ? "bg-ds-surface text-ds-violet-pop shadow-ds-xs"
                : "text-ds-ink-3 hover:text-ds-ink",
            )}
          >
            {t(tab.label)}
          </Link>
        );
      })}
    </nav>
  );
}
